import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { FilmTile } from "../FilmTile/FilmTile.tsx";
import { CombinedFilmObject, SortOption } from "../../types/dataTypes.ts";
import { FilmSearchBar } from "../SearchBars/FilmSearchBar.tsx";
import { sortScreenings } from "../../helpers/componentHelpers.ts";
import { ManualAd } from "../ManualAd/ManualAd.tsx";
import { ADSENSE_SLOTS } from "../../consts/adsenseAdSlots.ts";

const INITIAL_IMAGE_EAGER_COUNT = 5;

// Only used when optimise === true
const INITIAL_VISIBLE_COUNT = 10;
const CHUNK_SIZE = 10;

//Ad details
const AD_EVERY_N = 4;
const AD_START_AFTER = 4;
const AD_MIN_HEIGHT = 250;
//const MAX_ADS = 10;

function shouldInsertAd(index: number) {
  //BELOW: TEMPORARY AD DISABLE
  return false;
  // const pos = index + 1;
  // if (pos < AD_START_AFTER) return false;

  // const isAdPosition =
  //   pos === AD_START_AFTER || (pos - AD_START_AFTER) % AD_EVERY_N === 0;
  // if (!isAdPosition) return false;

  // const adOrdinal = Math.floor((pos - AD_START_AFTER) / AD_EVERY_N); // 0,1,2,...
  // return adOrdinal < MAX_ADS;
}

function getAdSlotForPosition(index: number) {
  const pos = index + 1;
  const adOrdinal = Math.floor((pos - AD_START_AFTER) / AD_EVERY_N); // 0,1,2...
  return ADSENSE_SLOTS.FILM_LIST[adOrdinal % ADSENSE_SLOTS.FILM_LIST.length];
}

export const FilmTab: React.FC<{
  filmData: CombinedFilmObject[];
  tabDateLabel: string;
  sortOption: SortOption;
  optimise: boolean;
}> = ({ filmData, tabDateLabel, sortOption = "alphabetical", optimise }) => {
  // Keep selection as a title, not a whole array (avoids re-sort loops + heavy state churn)
  const [selectedTitle, setSelectedTitle] = React.useState<string | null>(null);

  const filmTitles = React.useMemo(
    () => filmData.map((film) => film.display_film_title),
    [filmData],
  );

  const filteredFilms = React.useMemo(() => {
    if (!selectedTitle) return filmData;
    return filmData.filter((film) => film.display_film_title === selectedTitle);
  }, [filmData, selectedTitle]);

  const sortedFilms = React.useMemo(() => {
    return sortScreenings(filteredFilms, sortOption);
  }, [filteredFilms, sortOption]);

  // Infinite scroll state (only active when optimise === true)
  const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_COUNT);

  // Reset chunking when inputs change
  React.useEffect(() => {
    if (!optimise) return;
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [optimise, tabDateLabel, sortOption, selectedTitle, sortedFilms.length]);

  const visibleFilms = React.useMemo(() => {
    if (!optimise) return sortedFilms;
    return sortedFilms.slice(0, visibleCount);
  }, [optimise, sortedFilms, visibleCount]);

  const hasMore = optimise && visibleCount < sortedFilms.length;

  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!optimise) return;
    if (!hasMore) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    let ticking = false;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        if (ticking) return;
        ticking = true;

        setVisibleCount((curr) =>
          Math.min(curr + CHUNK_SIZE, sortedFilms.length),
        );

        queueMicrotask(() => {
          ticking = false;
        });
      },
      {
        root: null,
        rootMargin: "800px 0px",
        threshold: 0.01,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [optimise, hasMore, sortedFilms.length]);

  return (
    <Box>
      <FilmSearchBar
        filmTitles={filmTitles}
        onSelectFilm={setSelectedTitle}
        currentTabLabel={tabDateLabel}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: 0,
          margin: 0,
          marginTop: { xs: 2, md: 2 },
          width: "100%",
        }}
      >
        <Stack
          spacing={{ xs: 2, md: 5 }}
          sx={{ width: "100%", margin: "0px", padding: "0px" }}
        >


          {visibleFilms.flatMap((film, index) => {
            const lazy = index >= INITIAL_IMAGE_EAGER_COUNT;

            const filmNode = (
              <FilmTile
                key={`film_${film.title}_${tabDateLabel}`}
                title={film.display_film_title}
                description={film.description ?? ""}
                image_link={film.image_link}
                screenings={film.screenings_data}
                rating={film.rating}
                lengthInMinutes={film.length_in_minutes}
                tabDateLabel={tabDateLabel}
                lazy={lazy}
                optimise={optimise}
                forPopularFilmPage={false}
              />
            );

            if (!shouldInsertAd(index)) return [filmNode];

            return [
              filmNode,
              <Box
                key={`ad_after_${tabDateLabel}_${index + 1}`}
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <ManualAd
                  adSlot={getAdSlotForPosition(index)}
                  minHeight={AD_MIN_HEIGHT}
                />
              </Box>,
            ];
          })}

          {optimise ? <div ref={sentinelRef} /> : null}

          {optimise ? (
            <Box sx={{ textAlign: "center", opacity: 0.7, py: 2 }}>
              {hasMore
                ? `Loading more… (${visibleFilms.length}/${sortedFilms.length})`
                : `Showing all Films (${sortedFilms.length})`}
            </Box>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};
